import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { DialogService } from 'primeng/dynamicdialog';

import { ConfirmDismissDialogComponent } from 'app/modules/shared/components/confirm-dismiss-dialog/confirm-dismiss-dialog.component';
import { HelperService } from 'app/services/helper.service';
import { FileService } from 'app/services/file.service';
import { RenameAttachmentDialogComponent } from 'app/modules/app-components/rename-attachment-dialog/rename-attachment-dialog.component';

import { environment } from 'environments/environment';

import { Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie';

@Injectable({
  providedIn: 'root',
})
export class AttachmentService {
  private token;
  private headers = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  /**
   * @todo create a generic http service which is responsible for all
   * header fetching etc
   */
  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private helperService: HelperService,
    private router: Router,
    private dialogService: DialogService,
    private fileService: FileService
  ) {}

  /**
   * ==============================
   *          Helper Methods
   *  ==============================
   */

  private getHeaders() {
    const token: string = <string>(<unknown>this.cookieService.getObject('alpat'));
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      authorization: token || null,
    });
    return headers;
  }

  private getFileHeaders() {
    this.token = this.cookieService.getObject('alpat');
    this.headers = new HttpHeaders({
      authorization: this.token ? this.token : null,
    });
    return this.headers;
  }

  private successHandler(res) {
    const d = res;
    try {
      if (d.status) {
        return Promise.resolve(d.response);
      }
    } catch (err) {
      console.error('Success Handler catch', err);
    }
  }

  private errorHandler(result: any) {
    try {
      if (!result.error.status) {
        if (result.status === 440) {
          this.helperService.logout();
        }

        return Promise.reject(result.error.error);
      }
    } catch (err) {
      console.error('Error Handler catch', err);
    }
  }

  async openAttachmentPreviewDL(
    attachment: {
      name: string;
      fileType: string;
      fileExtension?: string;
    },
    token = null
  ): Promise<void> {
    try {
      // destruct required data
      const { name, fileType, fileExtension } = attachment;

      // check if all required params present
      if (!name || !fileType) {
        throw new Error('Attachment has incomplete data to open preview');
      }

      // before opening, get signed url for that file
      const signedUrl: string = await this.fileService.getSignedUrlDL({
        key: name,
        token,
      });

      // check file type and handle file preview based on that
      switch (fileType) {
        // open image
        case 'IMAGE':
          {
            this.fileService.openImagePreview({ name, url: signedUrl, type: fileType.toLowerCase() });
          }
          break;

        // open video
        case 'VIDEO':
          {
            const extensions = ['avi', 'wmv', 'flv', 'f4v', 'swf', 'mpeg2', 'mpeg', 'mpg'];
            if (fileExtension && !extensions.includes(fileExtension)) {
              this.fileService.openVideoPreview({ name, url: signedUrl });
            }
          }
          break;

        // any other file type, open browser default
        default:
          {
            if (this.helperService.isAppleDevice()) {
              window.location.assign(signedUrl);
            } else {
              window.open(signedUrl, '_blank');
            }
          }
          break;
      }
    } catch (error) {
      this.helperService.showToaster('error', error);
    }
  }

  /**
   * Open attachment preview
   */
  async openAttachmentPreview(
    attachment: {
      name: string;
      fileType: string;
      fileExtension?: string;
    },
    token = null
  ): Promise<void> {
    try {
      // destruct required data
      const { name, fileType, fileExtension } = attachment;

      // check if all required params present
      if (!name || !fileType) {
        throw new Error('Attachment has incomplete data to open preview');
      }

      let signedUrl: string;
      // before opening, get signed url for that file
      if (!token) {
        signedUrl = await this.fileService.getSignedUrl({
          key: name,
          token,
        });
      } else {
        signedUrl = await this.fileService.getSignedUrlForCustomer({
          key: name,
          token,
        });
      }

      // check file type and handle file preview based on that
      switch (fileType) {
        // open image
        case 'IMAGE':
          {
            this.fileService.openImagePreview({ name, url: signedUrl, type: fileType.toLowerCase() });
          }
          break;

        // open video
        case 'VIDEO':
          {
            const extensions = ['avi', 'wmv', 'flv', 'f4v', 'swf', 'mpeg2', 'mpeg', 'mpg'];
            if (fileExtension && !extensions.includes(fileExtension)) {
              this.fileService.openVideoPreview({ name, url: signedUrl });
            }
          }
          break;

        // any other file type, open browser default
        default:
          {
            const link = document.createElement('a');
            link.href = signedUrl;

            const filename = signedUrl.split('/').pop();
            link.download = filename.includes('.') ? filename : `${filename}.${fileExtension}`;

            if (this.helperService.isAppleDevice()) {
              window.location.assign(signedUrl);
            } else {
              link.target = '_blank';
              link.click();
            }
          }
          break;
      }
    } catch (error) {
      this.helperService.showToaster('error', error);
    }
  }

  /**
   * Open attachment rename dialog
   *
   * @returns if update successful
   */
  openAttachmentRenameDialog(attachment: { _id: string; title: string; fileExtension: string }): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        // open dialog
        const ref = this.dialogService.open(RenameAttachmentDialogComponent, {
          styleClass: 'small-heading-dialog rename-modal',
          width: '400px',
          showHeader: false,
          data: { attachment },
        });

        // on attachment rename success return success
        const onClose = ref.onClose.subscribe((close: any) => {
          // unsubscribe dialog listener
          onClose.unsubscribe();

          // resolve with status
          resolve(close.isSuccess);
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Remove/delete an attachment from order
   *
   * @param attachmentId
   * @returns is success
   */
  handleAttachmentDelete(attachmentId: string): Observable<boolean> {
    return Observable.create((observer) => {
      try {
        const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
          styleClass: 'small-heading-dialog',
          width: '35%',
          showHeader: false,
          data: {
            title: 'Are you sure you want to delete?',
            message: 'Customers will not be able to view the attachments once they are deleted',
            button: {
              success: { title: 'Delete' },
              cancel: { title: 'Cancel' },
            },
          },
        });

        const onClose = ref.onClose.subscribe(async (close: any) => {
          // unsubscribe subscription
          onClose.unsubscribe();

          // on no, just exit func
          if (!close.isSuccess) {
            observer.next(false);
            observer.complete();
          }

          // on yes, remove and return true
          await this.removeAttachment(attachmentId);
          observer.next(true);
          observer.complete();
        });
      } catch (err) {
        observer.error(err);
        observer.complete();
      }
    });
  }

  /**
   * Open delete attachment dialog
   *
   * @param attachmentId
   * @returns is success
   */
  openAttachmentDeleteDialog(): void {
    this.dialogService.open(ConfirmDismissDialogComponent, {
      styleClass: 'small-heading-dialog',
      width: '35%',
      showHeader: false,
      data: {
        title: 'Attachment not found',
        message: 'Oops!! Looks like the attachment has been removed from the system',
        button: {
          cancel: { title: 'Cancel' },
        },
      },
    });
  }

  /**
   * Get all attachments against order
   * @param estimateId
   * @param data
   * @returns
   */
  getOrderAttachment(
    estimateId: string,
    query?: {
      limit?: number;
      skip?: number;
      sortField?: string;
      sortOrder?: number;
      searchQuery?: string;
      category?: string;
    }
  ): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/attachment/${estimateId}`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  /**
   * Get order attachment count
   * @param estimateId
   * @param query
   * @returns
   */
  getOrderAttachmentCount(estimateId: string, query: any = {}): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/attachment/${estimateId}/count`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  /**
   * Update attachment
   *
   * @param attachmentId
   * @returns updated order
   */
  createAttachment(estimateId: string, formData: FormData): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/attachment/${estimateId}`, this.helperService.encryptData(formData), { headers: this.getFileHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /**
   * Remove attachment from order
   *
   * @param attachmentId
   * @returns updated order
   */
  removeAttachment(attachmentId: string): Promise<any> {
    return this.http
      .delete(`${environment.api_base_url}/attachment/${attachmentId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }
}
